import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFlowOwner } from "@/components/FlowAccessGate";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getGeoData } from "@/utils/geolocation";
import { toast } from "sonner";

type Flow = {
  id: string;
  business_name: string;
  product_name: string;
  logo_url: string | null;
  product_image_url: string | null;
  flow_type: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  referral_reward?: string;
  referral_count?: string;
  referral_heading?: string;
  referral_subtitle?: string;
  referral_cta_text?: string;
  referral_color?: string;
  referral_step_color?: string;
  redirect_url?: string;
  thank_you_message?: string;
};

export default function ReferralFlow() {
  const { flowId } = useParams();
  const navigate = useNavigate();
  const { isPaid } = useFlowOwner();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [flow, setFlow] = useState<Flow | null>(null);
  const [email, setEmail] = useState("");

  useEffect(() => {
    const fetchFlow = async () => {
      try {
        if (!flowId) return;
        
        const { data, error } = await supabase
          .from('flows')
          .select('*')
          .eq('id', flowId)
          .single();
        
        if (error) {
          throw error;
        }
        
        setFlow(data);
        recordFlowVisit();
      } catch (error) {
        toast.error('Failed to load flow data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFlow();
  }, [flowId]);

  const recordFlowVisit = async () => {
    if (!flowId) return;
    
    try {
      // Generate a unique key for this flow view
      const viewKey = `flow_view_${flowId}`;
      
      // Check if this user has already viewed this flow today
      const viewDate = new Date().toISOString().split('T')[0]; // Get current date in YYYY-MM-DD format
      const storedView = localStorage.getItem(viewKey);
      
      // If user already viewed this flow today, don't record again
      if (storedView === viewDate) {
        return;
      }
      
      // Store the view in localStorage to prevent duplicates
      localStorage.setItem(viewKey, viewDate);
      
      // Generate a unique identifier for the user if not exists
      let userIdentifier = localStorage.getItem('flow_user_id');
      if (!userIdentifier) {
        userIdentifier = crypto.randomUUID();
        localStorage.setItem('flow_user_id', userIdentifier);
      }
      
      const geoData = await getGeoData();
      
      await supabase.from('flow_analytics').insert({
        flow_id: flowId,
        event_type: 'view',
        referrer: document.referrer || 'direct',
        is_embedded: window.self !== window.top,
        user_agent: navigator.userAgent,
        path: window.location.pathname,
        country: geoData?.country || 'Unknown',
        user_identifier: userIdentifier,
        view_date: viewDate
      });
    } catch (error) {
      // Failed to record view analytics
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (!email.trim()) {
        throw new Error('Please enter your email address');
      }

      const geoData = await getGeoData();
      
      // Update flow to ensure flow_type is 'referral'
      if (flow && flow.flow_type !== 'referral') {
        const { error } = await supabase
          .from('flows')
          .update({ flow_type: 'referral' })
          .eq('id', flowId);

        if (error) {
          // Error updating flow type
        }
      }

      // Generate a unique identifier for the user if not exists
      let userIdentifier = localStorage.getItem('flow_user_id');
      if (!userIdentifier) {
        userIdentifier = crypto.randomUUID();
        localStorage.setItem('flow_user_id', userIdentifier);
      }
      
      // Store email in session storage for the thank you page
      sessionStorage.setItem('referral_email', email);
      
      await supabase.from('flow_analytics').insert({
        flow_id: flowId,
        email: email,
        event_type: 'conversion',
        referrer: document.referrer || 'direct',
        is_embedded: window.self !== window.top,
        user_agent: navigator.userAgent,
        path: window.location.pathname,
        country: geoData?.country || 'Unknown',
        user_identifier: userIdentifier,
        view_date: new Date().toISOString().split('T')[0]
      });

      navigate(`/thank-you/${flowId}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to submit');
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!flow) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Flow not found</h1>
          <p className="text-muted-foreground">The requested flow could not be found.</p>
        </div>
      </div>
    );
  }

  const accent = flow.referral_color || "#a855f7";
  const accentLight = (() => {
    const num = parseInt(accent.replace("#", ""), 16);
    const amt = Math.round(2.55 * 45);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;
    return "#" + (0x1000000 + (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 + (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 + (B < 255 ? (B < 1 ? 0 : B) : 255)).toString(16).slice(1);
  })();
  const isLight = (() => {
    const num = parseInt(accent.replace("#", ""), 16);
    return (num >> 16) * 0.299 + ((num >> 8) & 0xff) * 0.587 + (num & 0xff) * 0.114 > 186;
  })();
  const accentTextColor = isLight ? "#111827" : "#ffffff";

  const stepColor = flow.referral_step_color || accent;
  const stepIsLight = (() => {
    const c = stepColor.replace("#", "");
    if (c.length !== 6) return false;
    const num = parseInt(c, 16);
    return (num >> 16) * 0.299 + ((num >> 8) & 0xff) * 0.587 + (num & 0xff) * 0.114 > 186;
  })();
  const stepTextColor = stepIsLight ? "#111827" : "#ffffff";

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-0 overflow-hidden">
      <div className="w-full max-w-3xl mx-auto">
        <Card className="shadow-lg border border-gray-100 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Left Column - Branding & Info */}
            <div className="bg-gray-50 p-8 flex flex-col justify-between">
              <div>
                {/* Program Name/Logo */}
                <div className="mb-8">
                  {flow.logo_url ? (
                    <img
                      src={flow.logo_url}
                      alt={flow.business_name || flow.product_name}
                      className="h-8 w-auto object-contain mb-4"
                    />
                  ) : (
                    <div className="font-medium text-lg mb-4" style={{ color: isLight ? "#111827" : accent }}>
                      {flow.business_name || flow.product_name}
                    </div>
                  )}
                </div>

                {/* Headline */}
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{flow.referral_heading || "Join our referral program"}</h2>
                <p className="text-gray-600 mb-8">
                  {flow.referral_subtitle || `Share ${flow.product_name} with others and earn rewards`}
                </p>

                {/* Referral Reward Box */}
                {flow.referral_reward && (
                  <div className="bg-white rounded-xl border border-gray-200 p-5 mb-8">
                    <div className="flex items-center">
                      <div className={`rounded-full p-3 mr-4 ${isLight ? "border border-gray-200" : ""}`} style={{ backgroundColor: isLight ? "#f9fafb" : accentLight }}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" style={{ color: isLight ? "#6b7280" : accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600 font-medium">Your Reward</div>
                        <div className="text-xl font-bold" style={{ color: isLight ? "#111827" : accent }}>
                          {flow.referral_reward}
                        </div>
                        {flow.referral_count && (
                          <div className="text-sm text-gray-500 mt-0.5">
                            After {flow.referral_count} successful referrals
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="p-8">
              <form onSubmit={handleSubmit} className="h-full flex flex-col">
                <div className="flex-grow">
                  {/* Steps */}
                  <div className="flex items-center justify-between mb-8">
                    {["Join", "Share", "Earn rewards"].map((label, i) => (
                      <React.Fragment key={label}>
                        {i > 0 && (
                          <div className="flex items-center px-2">
                            <div
                              className="w-8 h-px"
                              style={{ backgroundColor: stepIsLight ? "#e5e7eb" : stepColor, opacity: stepIsLight ? 1 : 0.35 }}
                            />
                          </div>
                        )}
                        <div className="flex flex-col items-center">
                          <div
                            className={`flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full text-sm font-semibold mb-1 ${stepIsLight ? "border border-gray-200" : ""}`}
                            style={{ backgroundColor: stepColor, color: stepTextColor }}
                          >
                            {i + 1}
                          </div>
                          <div className="text-xs text-gray-700">{label}</div>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>

                  <h3 className="text-lg font-medium text-gray-900 mb-6">Enter your email</h3>

                  <div className="space-y-2 mb-2">
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-12 px-4 rounded-lg border-gray-200 text-base bg-white"
                      style={{ ["--tw-ring-color" as string]: accent }}
                    />
                  </div>

                  <p className="text-sm text-gray-500 mb-8">
                    We'll send you details about the program
                  </p>

                  <Button
                    type="submit"
                    className={`w-full font-medium h-12 rounded-lg text-base transition-opacity hover:opacity-90 ${isLight ? "border border-gray-300" : ""}`}
                    style={{ backgroundColor: accent, color: accentTextColor }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      flow.referral_cta_text || "Join Referral Program"
                    )}
                  </Button>
                </div>
                
                <div className="flex items-center justify-center mt-6 text-sm text-gray-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-500 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Your data is secure and private
                </div>
              </form>
            </div>
          </div>
        </Card>

        {!isPaid && (
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-400">
              Powered by <a href="https://willtheyconvert.com/" className="text-gray-500 hover:text-gray-700">willtheyconvert.com</a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
} 